import vendorProductService from "../services/vendorProduct.service";
import { toogleErrorMessage, toogleSuccessMessage } from "./alertMessage";
import {
  VENDOR_PRODUCTS_LIST,
  VENDOR_PRODUCTS_LOADING,
  VENDOR_PRODUCTS_ERROR,
  VENDOR_PRODUCT_DETAIL,
  VENDOR_PRODUCT_DETAIL_LOADING,
  VENDOR_PRODUCT_DETAIL_ERROR,
  VENDOR_PRODUCT_MUTATION_LOADING,
  VENDOR_PRODUCT_MUTATION_ERROR,
  VENDOR_PRODUCT_SECTORS,
  VENDOR_PRODUCT_SECTORS_LOADING,
  VENDOR_PRODUCT_SECTORS_ERROR,
  VENDOR_PRODUCT_SECTOR_ITEMS,
  VENDOR_PRODUCT_SECTOR_LOADING,
  VENDOR_PRODUCT_SECTOR_ERROR,
} from "./type";

const statusOf = (response) => response?.status ?? response?.response?.status;
const responseBodyOf = (response) => response?.data || response?.response?.data || {};
const explicitlyRejected = (response) => {
  const body = responseBodyOf(response);
  return body.status === false || body.success === false ||
    body.data?.status === false || body.data?.success === false;
};
const succeeded = (response) =>
  statusOf(response) >= 200 && statusOf(response) < 300 && !explicitlyRejected(response);
export const isVendorProductResponseSuccessful = succeeded;
const messageOf = (response) =>
  response?.data?.message ||
  response?.data?.data?.message ||
  response?.data?.data?.data?.message ||
  response?.response?.data?.message ||
  response?.response?.data?.data?.message ||
  response?.response?.data?.data?.data?.message ||
  response?.message ||
  "";
const collection = (response, keys = []) => {
  const body = response?.data || {};
  const candidates = [body?.data?.data, body?.data, body];
  const container =
    candidates.find(
      (value) => value && typeof value === "object" && !Array.isArray(value),
    ) || {};
  const items =
    candidates.find(Array.isArray) ||
    keys.map((key) => container?.[key]).find(Array.isArray) ||
    container?.items ||
    container?.results ||
    container?.list ||
    container?.data ||
    [];
  const pagination =
    candidates.map((value) => value?.pagination || value?.meta).find(Boolean) ||
    {};
  return {
    body,
    container,
    items: Array.isArray(items) ? items : [],
    pagination,
  };
};
const detail = (response) => {
  const body = response?.data || {};
  const item =
    body?.data?.product ||
    body?.data?.vendorProduct ||
    body?.data?.item ||
    body?.data?.data ||
    body?.data ||
    body?.product ||
    body?.vendorProduct ||
    body?.item ||
    body;
  return Array.isArray(item) ? item[0] || null : item;
};
const paginationData = (pageData, container, body, page, limit, count) => {
  const currentPage =
    Number(
      pageData.page ??
        pageData.currentPage ??
        pageData.current_page ??
        container?.page ??
        body?.page,
    ) || Number(page);
  const pageSize =
    Number(
      pageData.limit ??
        pageData.pageSize ??
        pageData.perPage ??
        container?.limit,
    ) || Number(limit);
  const reportedTotal =
    pageData.total ??
    pageData.totalCount ??
    pageData.totalItems ??
    pageData.totalRecords ??
    pageData.totalDocs ??
    container?.total ??
    body?.total;
  const reportedPages = Number(
    pageData.totalPages ??
      pageData.total_pages ??
      pageData.pages ??
      container?.totalPages ??
      body?.totalPages,
  );
  const total =
    reportedTotal == null
      ? reportedPages
        ? Math.max((reportedPages - 1) * pageSize + count, count)
        : count
      : Number(reportedTotal) || 0;
  return {
    ...pageData,
    page: currentPage,
    limit: pageSize,
    total,
    totalPages: reportedPages || Math.max(Math.ceil(total / pageSize), 1),
  };
};

export const getVendorProductsAction =
  ({ page = 1, limit = 10 } = {}) =>
  async (dispatch) => {
    dispatch({ type: VENDOR_PRODUCTS_LOADING, payload: true });
    dispatch({ type: VENDOR_PRODUCTS_ERROR, payload: "" });
    try {
      const response = await vendorProductService.getVendorProducts({
        page,
        limit,
      });
      if (succeeded(response)) {
        const result = collection(response, [
          "products",
          "vendorProducts",
          "items",
          "results",
          "list",
        ]);
        dispatch({
          type: VENDOR_PRODUCTS_LIST,
          payload: {
            items: result.items,
            pagination: paginationData(
              result.pagination,
              result.container,
              result.body,
              page,
              limit,
              result.items.length,
            ),
          },
        });
      } else {
        dispatch({
          type: VENDOR_PRODUCTS_ERROR,
          payload: messageOf(response) || "Unable to load your products.",
        });
      }
      return response;
    } finally {
      dispatch({ type: VENDOR_PRODUCTS_LOADING, payload: false });
    }
  };

export const getVendorProductAction = (id) => async (dispatch) => {
  dispatch({ type: VENDOR_PRODUCT_DETAIL, payload: null });
  dispatch({ type: VENDOR_PRODUCT_DETAIL_ERROR, payload: "" });
  dispatch({ type: VENDOR_PRODUCT_DETAIL_LOADING, payload: true });
  try {
    const response = await vendorProductService.getVendorProduct(id);
    if (succeeded(response)) {
      dispatch({ type: VENDOR_PRODUCT_DETAIL, payload: detail(response) });
    } else {
      dispatch({
        type: VENDOR_PRODUCT_DETAIL_ERROR,
        payload: messageOf(response) || "Unable to load product details.",
      });
    }
    return response;
  } finally {
    dispatch({ type: VENDOR_PRODUCT_DETAIL_LOADING, payload: false });
  }
};

export const getVendorSectorsAction = () => async (dispatch) => {
  dispatch({ type: VENDOR_PRODUCT_SECTORS_LOADING, payload: true });
  dispatch({ type: VENDOR_PRODUCT_SECTORS_ERROR, payload: "" });
  try {
    const response = await vendorProductService.getSectors();
    if (succeeded(response)) {
      dispatch({
        type: VENDOR_PRODUCT_SECTORS,
        payload: collection(response, ["sectors", "items", "results", "list"])
          .items,
      });
    } else {
      dispatch({
        type: VENDOR_PRODUCT_SECTORS_ERROR,
        payload: messageOf(response) || "Unable to load sectors.",
      });
    }
    return response;
  } finally {
    dispatch({ type: VENDOR_PRODUCT_SECTORS_LOADING, payload: false });
  }
};

export const getVendorSectorProductsAction =
  (sectorId) => async (dispatch, getState) => {
    const key = String(sectorId || "");
    if (!key) return null;
    const state = getState().vendorProduct;
    if (
      state?.sectorProductsLoaded?.[key] ||
      state?.sectorProductsLoading?.[key]
    )
      return null;
    dispatch({
      type: VENDOR_PRODUCT_SECTOR_LOADING,
      payload: { sectorId: key, loading: true },
    });
    dispatch({
      type: VENDOR_PRODUCT_SECTOR_ERROR,
      payload: { sectorId: key, error: "" },
    });
    try {
      const response = await vendorProductService.getSectorProducts(key);
      if (succeeded(response)) {
        dispatch({
          type: VENDOR_PRODUCT_SECTOR_ITEMS,
          payload: {
            sectorId: key,
            items: collection(response, [
              "products",
              "items",
              "results",
              "list",
            ]).items,
          },
        });
      } else {
        dispatch({
          type: VENDOR_PRODUCT_SECTOR_ERROR,
          payload: {
            sectorId: key,
            error:
              messageOf(response) || "Unable to load products for this sector.",
          },
        });
      }
      return response;
    } finally {
      dispatch({
        type: VENDOR_PRODUCT_SECTOR_LOADING,
        payload: { sectorId: key, loading: false },
      });
    }
  };

export const createVendorProductsAction = (payloads) => async (dispatch) => {
  dispatch({ type: VENDOR_PRODUCT_MUTATION_LOADING, payload: true });
  dispatch({ type: VENDOR_PRODUCT_MUTATION_ERROR, payload: "" });
  try {
    const responses = await Promise.all(
      payloads.map((payload) =>
        vendorProductService.createVendorProduct(payload),
      ),
    );
    const createdCount = responses.filter(succeeded).length;
    const failed = responses.find((response) => !succeeded(response));
    if (failed) {
      const message =
        messageOf(failed) ||
        (createdCount
          ? "Some selected products could not be added."
          : "Unable to add products.");
      dispatch({ type: VENDOR_PRODUCT_MUTATION_ERROR, payload: message });
      toogleErrorMessage(message);
    } else {
      toogleSuccessMessage(
        responses.length === 1
          ? "Product added successfully."
          : "Products added successfully.",
      );
    }
    return { responses, createdCount, success: !failed };
  } finally {
    dispatch({ type: VENDOR_PRODUCT_MUTATION_LOADING, payload: false });
  }
};

export const updateVendorProductAction = (id, payload) => async (dispatch) => {
  dispatch({ type: VENDOR_PRODUCT_MUTATION_LOADING, payload: true });
  dispatch({ type: VENDOR_PRODUCT_MUTATION_ERROR, payload: "" });
  try {
    const response = await vendorProductService.updateVendorProduct(
      id,
      payload,
    );
    if (succeeded(response)) {
      toogleSuccessMessage(
        messageOf(response) || "Product updated successfully.",
      );
    } else {
      const message = messageOf(response) || "Unable to update product.";
      dispatch({ type: VENDOR_PRODUCT_MUTATION_ERROR, payload: message });
      toogleErrorMessage(message);
    }
    return response;
  } finally {
    dispatch({ type: VENDOR_PRODUCT_MUTATION_LOADING, payload: false });
  }
};

export const deleteVendorProductAction = (id) => async (dispatch) => {
  dispatch({ type: VENDOR_PRODUCT_MUTATION_LOADING, payload: true });
  dispatch({ type: VENDOR_PRODUCT_MUTATION_ERROR, payload: "" });
  try {
    const response = await vendorProductService.deleteVendorProduct(id);
    if (succeeded(response)) {
      toogleSuccessMessage(
        messageOf(response) || "Product deleted successfully.",
      );
    } else {
      const message = messageOf(response) || "Unable to delete product.";
      dispatch({ type: VENDOR_PRODUCT_MUTATION_ERROR, payload: message });
      toogleErrorMessage(message);
    }
    return response;
  } finally {
    dispatch({ type: VENDOR_PRODUCT_MUTATION_LOADING, payload: false });
  }
};
